import { type RefObject, useLayoutEffect } from "react"

const SCROLL_AREA_VIEWPORT_SELECTOR = ".mantine-ScrollArea-viewport"
const ALIGNMENT_EPSILON_PX = 2
const MAX_SCROLL_ATTEMPTS = 40

/**
 * Aligns the bottom of `element` with the bottom of the nearest Mantine
 * ScrollArea viewport. Retries across frames because ZoomContainer updates
 * scrollHeight asynchronously after the DOM grows.
 */
export function scrollElementBottomIntoScrollArea(
  element: HTMLElement
): void {
  const viewport = element.closest(SCROLL_AREA_VIEWPORT_SELECTOR)

  if (!(viewport instanceof HTMLElement)) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    })
    return
  }

  let attempts = 0
  let hasStartedScroll = false

  const alignBottom = (): boolean => {
    const delta =
      element.getBoundingClientRect().bottom -
      viewport.getBoundingClientRect().bottom

    if (Math.abs(delta) <= ALIGNMENT_EPSILON_PX) {
      return true
    }

    const maxScrollTop = viewport.scrollHeight - viewport.clientHeight
    if (maxScrollTop <= 0 && delta > ALIGNMENT_EPSILON_PX) {
      // ZoomContainer has not expanded the scrollable area yet.
      return false
    }

    viewport.scrollBy({
      top: delta,
      behavior: hasStartedScroll ? "auto" : "smooth",
    })
    hasStartedScroll = true

    return Math.abs(delta) <= ALIGNMENT_EPSILON_PX
  }

  const tick = (): void => {
    attempts += 1
    const isAligned = alignBottom()
    if (!isAligned && attempts < MAX_SCROLL_ATTEMPTS) {
      requestAnimationFrame(tick)
    }
  }

  requestAnimationFrame(tick)
}

type UseScrollElementBottomIntoScrollAreaOptions = {
  readonly enabled: boolean
  readonly onScrolled?: () => void
}

/**
 * When `enabled` becomes true, scrolls `elementRef` so its bottom aligns with
 * the nearest ScrollArea viewport bottom.
 */
export function useScrollElementBottomIntoScrollArea(
  elementRef: RefObject<HTMLElement | null>,
  { enabled, onScrolled }: UseScrollElementBottomIntoScrollAreaOptions
): void {
  useLayoutEffect(() => {
    if (!enabled || elementRef.current === null) {
      return
    }

    scrollElementBottomIntoScrollArea(elementRef.current)
    onScrolled?.()
  }, [enabled, elementRef, onScrolled])
}
